/**
 * Deep-link builders for Teams, tel: and mailto:.
 *
 * Email addresses and phone numbers were interpolated straight into URL
 * templates in ten places. Directory data comes from Azure AD and Vantagepoint
 * rather than from users, so this was not an XSS route — React escapes attribute
 * values, and a `tel:`/`https:` prefix cannot be escaped into `javascript:` —
 * but it was still wrong:
 *
 * - an address containing `&` or `#` injects extra query parameters into the
 *   Teams deep link, so the wrong person (or nobody) gets opened
 * - a stray space produces a URL the OS handler silently refuses
 * - a malformed or empty value produced a live link to nowhere
 *
 * Each builder validates the shape and returns null when it cannot produce a
 * meaningful link, so callers render nothing instead of a broken affordance.
 */

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Null unless the value looks like a single address. */
export function normalizeEmail(email) {
  const value = String(email ?? '').trim();
  return EMAIL_SHAPE.test(value) ? value : null;
}

/**
 * Phone numbers arrive formatted for humans ("(817) 555-0142 x21"). tel: accepts
 * digits, a leading +, and separators; anything else is dropped rather than
 * guessed at.
 */
export function normalizePhone(phone) {
  const raw = String(phone ?? '').trim();
  if (!raw) return null;
  const leadingPlus = raw.startsWith('+') ? '+' : '';
  const digits = raw.replace(/[^\d]/g, '');
  if (digits.length < 4) return null;
  return `${leadingPlus}${digits}`;
}

export function teamsChatUrl(email) {
  const value = normalizeEmail(email);
  return value ? `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(value)}` : null;
}

export function teamsCallUrl(email) {
  const value = normalizeEmail(email);
  return value ? `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(value)}` : null;
}

export function teamsMeetingUrl(emails = []) {
  const list = emails.map(normalizeEmail).filter(Boolean);
  if (list.length === 0) return null;
  return `https://teams.microsoft.com/l/meeting/new?attendees=${encodeURIComponent(list.join(','))}`;
}

export function telUrl(phone) {
  const value = normalizePhone(phone);
  return value ? `tel:${value}` : null;
}

export function mailtoUrl(email) {
  const value = normalizeEmail(email);
  return value ? `mailto:${encodeURIComponent(value)}` : null;
}
